import { useCallback, useState } from "react";

import { defineMessage, useIntl } from "react-intl";

import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import FindMyResultReturnValue from "@mapmycustomers/shared/types/FindMyResultReturnValue";
import geoService, {
  convertCoordinatesToLongLat,
} from "@mapmycustomers/shared/util/geo/GeoService";

const findMeFailedMessage = defineMessage({
  id: "useFindMyLocation.error.findMeFailed",
  defaultMessage: "Failed to find your location, check browser permissions and try again please",
  description: "Find current user position failed message in Create Route Modal",
});

const failedGeocodeAddress = defineMessage({
  id: "useFindMyLocation.error.geocodingFailed",
  defaultMessage: "Failed to find location address, please try another one",
  description: "Geocoding failed message in Create Route Modal",
});

const useFindMyLocation = (
  reverseGeocode: (payload: {
    callback: (result: GeocodeResult) => void;
    coordinates: LongLat;
    failureCallback?: () => void;
  }) => void
): FindMyResultReturnValue => {
  const intl = useIntl();
  const [location, setLocation] = useState<string>("");
  const [geocoderResult, setGeocoderResult] = useState<GeocodeResult | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [geocodingError, setGeocodingError] = useState<string | undefined>();
  const handleFindMyLocation = useCallback(() => {
    setLoading(true);
    geoService
      .getCurrentPosition()
      .then((position) => {
        reverseGeocode({
          callback: (result) => {
            const { address, city, country, postalCode } = result.address;
            const formattedAddress = [address, city, country, postalCode]
              .filter((item) => !!item)
              .join(", ");
            setLocation(formattedAddress);
            setGeocoderResult(result);
            setLoading(false);
          },
          coordinates: convertCoordinatesToLongLat(position.coords),
          failureCallback: () => {
            setGeocodingError(intl.formatMessage(failedGeocodeAddress));
            setLoading(false);
          },
        });
      })
      .catch(() => {
        setGeocodingError(intl.formatMessage(findMeFailedMessage));
        setLoading(false);
      });
  }, [intl, setLocation, setGeocodingError, reverseGeocode]);

  const resetError = useCallback(() => {
    setGeocodingError(undefined);
  }, []);

  return [
    location,
    setLocation,
    loading,
    geocodingError,
    handleFindMyLocation,
    geocoderResult,
    resetError,
  ];
};

export default useFindMyLocation;
